import React from 'react';
import { connect } from 'react-redux';
import { withStyles, Typography } from '@material-ui/core';
import { hasPermission } from '../../../sc-utilities';
import ProductsList from './ProductsList';
import { NavLink } from 'react-router-dom';
import { getErasureStatuses, getVerificationStatuses } from '../../../sc-product/product.utilities';

const styles = (theme) => ({
    '@global': {
        "[class*='productButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            padding: '10px 30px',
            textDecoration: 'none',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
            borderRadius: '5px',
        },
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
    productButtons: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.buttonText.default,
        margin: theme.spacing(),
        padding: '10px 30px',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        borderRadius: '5px',
    },
});

const Products = (props) => {
    const {
        classes,
        activeExternalServiceAvailable,
        translations,
        permissions,
        columns,
        tableRef,
        fetchTableData,
        tableOptions,
    } = props;

    let visible = columns && columns['Products'];

    const goToProduct = (productID, route, type) => {
        return `/${route}/details?productID=${productID}&type=${type}`;
    };

    const createProductAction = (product, paletteType) => {
        let path;
        switch (paletteType) {
            case 'PCProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'pc-product', paletteType)
                    : null;
                break;
            case 'MobileProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'mobile-product', paletteType)
                    : null;
                break;
            case 'StorageProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'storage-product', paletteType)
                    : null;
                break;
            default:
                path = '';
        }

        return product[`InventoryID`] ? (
            <NavLink to={path ?? ''} className={classes.productButtons}>
                {product['InventoryID']}
            </NavLink>
        ) : (
            translations['short_not_assigned'] || 'n/a'
        );
    };

    let tableColumns = [
        {
            title: translations['inventory_id'] || 'Securaze ID',
            field: 'InventoryID',
            hidden: visible ? visible['InventoryID'] : false,
            filtering: false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            render: (rowData) => {
                return createProductAction(rowData, rowData.Type);
            },
        },
        {
            title: translations['type'] || 'Type',
            field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Type ? rowData.Type : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['serial_number'] || 'Serial number',
            field: 'SerialNumber',
            hidden: visible ? visible['SerialNumber'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.SerialNumber ? rowData.SerialNumber : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['imei'] || 'IMEI',
            field: 'IMEI',
            hidden: visible ? visible['IMEI'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.IMEI ? rowData.IMEI : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['imei_2'] || 'IMEI 2',
            field: 'IMEI2',
            hidden: visible ? visible['IMEI2'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.IMEI2 ? rowData.IMEI2 : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['model'] || 'Model',
            field: 'Model',
            hidden: visible ? visible['Model'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Model ? rowData.Model : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['vendor'] || 'Vendor',
            field: 'Vendor',
            hidden: visible ? visible['Vendor'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Vendor ? rowData.Vendor : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['external_id'] || 'ExternalID',
            field: 'ExternalID',
            hidden: visible ? visible['ExternalID'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.ExternalID ? rowData.ExternalID : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['external_service_type'] || 'External service type',
            field: 'ExternalServiceType',
            hidden: visible ? visible['ExternalServiceType'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },

            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.ExternalServiceType
                            ? rowData.ExternalServiceType
                            : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['diagnose'] || 'Diagnose',
            field: 'DiagnoseStatus',
            hidden: visible ? visible['DiagnoseStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'} style={{ color: rowData?.DiagnoseStatus?.C || 'inherit' }}>
                        {rowData?.DiagnoseStatus?.T
                            ? rowData.DiagnoseStatus.T
                            : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['erasure_status'] || 'Erasure status',
            field: 'ErasureStatus',
            hidden: visible ? visible['ErasureStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return getErasureStatuses(rowData, null, classes, translations);
            },
        },
        {
            title: translations['verification_status'] || 'Verification status',
            field: 'VerificationStatus',
            hidden: visible ? visible['VerificationStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return getVerificationStatuses(rowData, null, classes, translations);
            },
        },
        {
            title: translations['deleted'] || 'Deleted',
            field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}
                    </Typography>
                );
            },
        },
    ];

    if (!activeExternalServiceAvailable) {
        tableColumns.splice(7, 2);
    }

    return hasPermission(permissions, 'product_list') ? (
        <div className={classes.root}>
            <ProductsList
                tableColumns={tableColumns}
                tableRef={tableRef}
                fetchTableData={fetchTableData}
                tableOptions={tableOptions}
            />
        </div>
    ) : null;
};

const mapStateToProps = (state) => {
    return {
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        activeExternalServiceAvailable: state.core.login.activeExternalServiceAvailable,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

export default connect(mapStateToProps, null)(withStyles(styles)(Products));
